import i18next from "i18next";

import { LANGUAGES, resolveLanguage } from "./config";

/**
 * Locale-aware formatting helpers.
 *
 * The reason this file exists rather than reaching for `toLocaleString()`
 * inline: `toLocaleString()` with no argument formats against the *browser's*
 * locale, not the app's. A Vietnamese reader whose OS is set to English would
 * otherwise get "12/25/2026" and "1,109" on a page they have explicitly
 * switched to Vietnamese. Every date and number in the app must be told which
 * language it is being rendered into.
 */

function intlLocale(language?: string): string {
  return LANGUAGES[resolveLanguage(language)].intlLocale;
}

/** "12/25/2026" in English, "25/12/2026" in Vietnamese — day-first either way
 *  for vi-VN, which is the local convention, not a bug. */
export function formatDate(
  value: string | number | Date,
  options: Intl.DateTimeFormatOptions = { dateStyle: "medium" },
  language = i18next.language,
): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(intlLocale(language), options).format(date);
}

export function formatDateTime(
  value: string | number | Date,
  options: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" },
  language = i18next.language,
): string {
  return formatDate(value, options, language);
}

/** Numbers with locale grouping: "1,109" → "1.109" under vi-VN. */
export function formatNumber(
  value: number,
  options: Intl.NumberFormatOptions = {},
  language = i18next.language,
): string {
  return new Intl.NumberFormat(intlLocale(language), options).format(value);
}

export function formatPercent(
  ratio: number,
  options: Intl.NumberFormatOptions = { maximumFractionDigits: 0 },
  language = i18next.language,
): string {
  return new Intl.NumberFormat(intlLocale(language), {
    ...options,
    style: "percent",
  }).format(ratio);
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Relative time as an i18next formatter: `{{t, formatDistance: from}}`.
 *
 * Uses `Intl.RelativeTimeFormat` with `numeric: "auto"` so English gets
 * "yesterday" and "2 days ago", and Vietnamese gets "hôm qua" / "2 ngày trước"
 * rather than a hand-rolled English string with the number swapped.
 *
 * Deliberately *not* `Intl.DurationFormat` / relative time in words: it is
 * newer, and Safari shipped neither until 16.
 */
const RELATIVE_STEPS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * DAY],
  ["month", 30 * DAY],
  ["week", 7 * DAY],
  ["day", DAY],
  ["hour", HOUR],
  ["minute", MINUTE],
  ["second", 1],
];

export function formatDistance(
  value: string | number | Date,
  language = i18next.language,
): string {
  const timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime();
  if (Number.isNaN(timestamp)) return "";

  const diff = timestamp - Date.now();
  const abs = Math.abs(diff);

  // Under 60s: "just now" reads better than "12 seconds ago" in a feed of
  // timestamps, and below a minute the exact number is noise either way.
  if (abs < MINUTE) {
    return i18next.t("common.time.justNow", { lng: language });
  }

  const rtf = new Intl.RelativeTimeFormat(intlLocale(language), { numeric: "auto" });
  for (const [unit, ms] of RELATIVE_STEPS) {
    if (abs >= ms || unit === "second") {
      return rtf.format(Math.round(diff / ms), unit);
    }
  }
  return rtf.format(0, "second");
}

/**
 * Bundle of every formatter, bound to one language.
 *
 * Handed out by `useLanguage()` as `format` so a component reads
 * `format.number(count)` / `format.date(iso)` without threading the language
 * through every call — the common case is 3–4 numbers on one card, and the
 * failure mode of forgetting one is a page that is half-translated by accident.
 */
export function formattersFor(language: string) {
  return {
    date: (value: string | number | Date, options?: Intl.DateTimeFormatOptions) =>
      formatDate(value, options, language),
    dateTime: (value: string | number | Date, options?: Intl.DateTimeFormatOptions) =>
      formatDateTime(value, options, language),
    number: (value: number, options?: Intl.NumberFormatOptions) =>
      formatNumber(value, options, language),
    percent: (ratio: number, options?: Intl.NumberFormatOptions) =>
      formatPercent(ratio, options, language),
    relative: (value: string | number | Date) => formatDistance(value, language),
  };
}

export type Formatters = ReturnType<typeof formattersFor>;